import React, { useState, useEffect, useMemo } from "react";
import Box from "@mui/material/Box";
import { Typography } from "@mui/material";
import {
  ORDERS_EXPORT_ENDPOINT,
  ORDERS_LIST_ENDPOINT,
} from "../../config/urls";
import { useAuth } from "../../hooks/AuthProvider";
import Filter from "../Filter";
import { Download } from "@mui/icons-material";
import { ColorsConstants } from "../../cssUtils/constants";
import typography from "../../cssUtils/typography";
import { formatWithTzLabel } from "../../utils/dateUtils";
import ButtonOne from "../../common/normal-button";
import AppTable from "../../common/app-table";
import TableSearchInput from "../TableSearchInput";
import {
  resolveSearchQueryForApi,
  normalizeCombinedSearchInput,
  PHONE_SEARCH_ERROR_MESSAGE,
} from "../../utils/phoneSearch";

const columns = [
  { id: "order_id", label: "Customer Order ID", align: "left" },
  { id: "woo_order_id", label: "Woocomerce Order ID", align: "left" },
  { id: "customer_name", label: "Name", align: "left" },
  { id: "phone_no", label: "Phone Number", align: "left" },
  { id: "total_quantity", label: "Total Quantity", align: "left" },
  { id: "currency", label: "Currency", align: "left" },
  { id: "subtotal", label: "Subtotal", align: "left" },
  { id: "gst", label: "GST Charges", align: "left" },
  { id: "shipping_charges", label: "Shipping Charges", align: "left" },
  { id: "total", label: "Total", align: "left" },
  { id: "payment_status", label: "Order Status", align: "left" },
  { id: "order_date", label: "Order Date", align: "left" },
];

export default function OrdersListTable({ refreshKey, onNavigate }) {
  const { getValidToken } = useAuth();
  const [rowCount, setRowCount] = useState(0);
  const [controller, setController] = useState({
    page: 0,
    rowsPerPage: 20,
  });

  const [orders, setOrders] = useState([]);
  const [filterLoading, setFilterLoading] = useState(false);
  const [activeFilters, setActiveFilters] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [charCount, setCharCount] = useState(0);

  const tenant = localStorage.getItem("tenant");
  const showWooColumn = !["zetta", "zetta-computech", "360vr-mangobite"].includes(tenant);

  const filterFields = [
    { label: "Order ID", type: "text" },
    {
      label: "Order Status",
      type: "single_select_list",
      values: ["PENDING", "PAID", "FAILED"],
    },
    { label: "Phone", type: "phone" },
    { label: "Total", type: "text" },
    { label: "Updated At", type: "date" },
  ];

  const resolvedOrderSearch = useMemo(
    () => resolveSearchQueryForApi(searchQuery),
    [searchQuery],
  );

  const fetchData = async () => {
    if (resolvedOrderSearch.blockFetch) {
      setOrders([]);
      setRowCount(0);
      setFilterLoading(false);
      return;
    }
    let url = ORDERS_LIST_ENDPOINT;

    // Construct query parameters for server-side filtering and pagination
    let queryParams = new URLSearchParams({
      limit: controller.rowsPerPage.toString(),
      offset: (controller.page * controller.rowsPerPage).toString(),
      search_query: resolvedOrderSearch.apiSearchQuery,
    });

    // apply active filters dynamically
    activeFilters.forEach(({ field, operator, value }) => {
      field = field.toLowerCase().replaceAll(" ", "_");
      operator = operator.toLowerCase().replaceAll(" ", "_");
      queryParams.append(`${field}`, `${operator}:${value ?? ""}`);
    });

    url += `?${queryParams}`;

    const token = await getValidToken();
    const tenant = localStorage.getItem("tenant");

    try {
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          "X-Tenant-Id": tenant,
        },
      });
      if (!response.ok) {
        throw new Error("Failed to fetch data");
      }
      const jsonData = await response.json();

      setOrders(jsonData.results);
      setRowCount(jsonData.count);
    } catch (error) {
      console.error("Error fetching data:", error.message);
    } finally {
      setFilterLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [
    controller.page,
    controller.rowsPerPage,
    refreshKey,
    searchQuery,
    activeFilters,
  ]);

  // pagination
  const handlePageChange = (event, newPage) => {
    setController((prev) => ({
      ...prev,
      page: newPage,
    }));
  };

  const handleChangeRowsPerPage = (event) => {
    setController({
      rowsPerPage: parseInt(event.target.value, 20),
      page: 0,
    });
  };

  const handleFiltersChange = (filters) => {
    setFilterLoading(true);
    setActiveFilters(filters);
    setController({
      page: 0,
      rowsPerPage: 20,
    });
  };

  const updateSearchValue = (value) => {
    const normalized = normalizeCombinedSearchInput(value);
    if (normalized.length > 50) {
      return;
    }
    setSearchQuery(normalized);
    setCharCount(normalized.length);
    setController({
      page: 0,
      rowsPerPage: 20,
    });
  };

  const handleExport = async () => {
    const exportSearch = resolveSearchQueryForApi(searchQuery);
    if (exportSearch.blockFetch) {
      alert(PHONE_SEARCH_ERROR_MESSAGE);
      return;
    }
    let url = ORDERS_EXPORT_ENDPOINT;

    // Construct query parameters for server-side filtering and pagination
    let queryParams = new URLSearchParams({
      limit: controller.rowsPerPage.toString(),
      offset: (controller.page * controller.rowsPerPage).toString(),
      search_query: exportSearch.apiSearchQuery,
    });

    // apply active filters dynamically
    activeFilters.forEach(({ field, operator, value }) => {
      field = field.toLowerCase().replaceAll(" ", "_");
      operator = operator.toLowerCase().replaceAll(" ", "_");
      queryParams.append(`${field}`, `${operator}:${value ?? ""}`);
    });

    url += `?${queryParams}`;

    const token = await getValidToken();
    const tenant = localStorage.getItem("tenant");

    try {
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          "X-Tenant-Id": tenant,
        },
      });

      if (!response.ok) {
        try {
          const error = (await response.json()).error;
          alert(error);
          throw new Error(error);
        } catch {
          throw new Error("Failed to export data");
        }
      }

      // Handle CSV response - clone response for blob processing
      const responseClone = response.clone();
      const blob = await responseClone.blob();

      // Create a link element to download the file
      const urlBlob = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.style.display = "none";
      a.href = urlBlob;

      // Use the current date for the filename
      const today = new Date();
      const dateStr = today.toISOString().split("T")[0];
      a.download = `orders-${dateStr}.csv`;

      // Append to the document and trigger download
      document.body.appendChild(a);
      a.click();

      // Clean up
      window.URL.revokeObjectURL(urlBlob);
      document.body.removeChild(a);
      return; // Exit after successful CSV download
    } catch (error) {
      console.error("Error exporting data:", error.message);
      throw error;
    }
  };

  // Define table columns for AppTable
  const tableColumns = columns
    .filter((column) => showWooColumn || column.id !== "woo_order_id")
    .map((column) => ({
      id: column.id,
      label: column.label,
      align: column.align,
      render: (row) => {
        const orderItems = row.order_items;

        if (column.id === "order_id") {
          return (
            <Typography
              sx={{
                ...typography.bodyLarge,
                color: ColorsConstants.primaryBackgroundColor,
                cursor: "pointer",
                fontWeight: 500,
              }}
            >
              {row.code || row.id || "N/A"}
            </Typography>
          );
        }

        if (column.id === "woo_order_id") {
          return (
            <Typography sx={{ ...typography.bodyLarge, color: "#1D2330" }}>
              {row.platform_order_id || "N/A"}
            </Typography>
          );
        }

        if (column.id === "customer_name") {
          return (
            <Typography sx={{ ...typography.bodyLarge, color: "#1D2330" }}>
              {row.customer_name || "N/A"}
            </Typography>
          );
        }

        if (column.id === "phone_no") {
          return (
            <Typography sx={{ ...typography.bodyLarge, color: "#1D2330" }}>
              {row.phone_no ? `+${row.phone_no}` : "N/A"}
            </Typography>
          );
        }

        if (column.id === "total_quantity") {
          const totalQty =
            orderItems && orderItems.length > 0
              ? orderItems.reduce((sum, item) => sum + (item.quantity || 0), 0)
              : null;
          return (
            <Typography sx={typography.bodyLarge}>
              {totalQty ?? "N/A"}
            </Typography>
          );
        }

        if (column.id === "currency") {
          return (
            <Typography sx={typography.bodyLarge}>
              {row.currency || "N/A"}
            </Typography>
          );
        }

        if (column.id === "subtotal") {
          return (
            <Typography sx={typography.bodyLarge}>
              {row.subtotal ?? "N/A"}
            </Typography>
          );
        }

        if (column.id === "gst") {
          return (
            <Typography sx={typography.bodyLarge}>
              {row.gst ?? "N/A"}
            </Typography>
          );
        }

        if (column.id === "shipping_charges") {
          return (
            <Typography sx={typography.bodyLarge}>
              {row.shipping_charges ?? "N/A"}
            </Typography>
          );
        }

        if (column.id === "total") {
          return (
            <Typography sx={typography.bodyLarge}>
              {row.total ?? "N/A"}
            </Typography>
          );
        }

        if (column.id === "payment_status") {
          return (
            <Typography sx={typography.bodyLarge}>
              {row.payment_status || "N/A"}
            </Typography>
          );
        }

        if (column.id === "order_date") {
          return (
            <Typography sx={typography.bodyLarge}>
              {formatWithTzLabel(row.created_at_iso, "DD-MM-YYYY hh:mm A")}
            </Typography>
          );
        }

        return <Typography sx={typography.bodyLarge}>N/A</Typography>;
      },
    }));

  const rowSx = (row, index) => ({
    backgroundColor: index % 2 === 0 ? "#F0F5FF" : "#FFFFFF",
    cursor: "pointer",
    "&:hover": {
      backgroundColor: index % 2 === 0 ? "#F5F5F5" : "#F7F9FC",
    },
    "& td": {
      borderBottom: "none",
      borderTop: "none",
    },
  });

  return (
    <Box sx={{ p: 3, bgcolor: "#fafafa" }}>
      {/* Header Section with Search, Filter and Export */}
      <Box
        sx={{
          mb: 3,
          display: "flex",
          alignItems: "center",
          gap: 2,
          width: "100%",
        }}
      >
        {/* Search - Takes remaining width */}
        <Box sx={{ flex: 1 }}>
          <TableSearchInput
            searchQuery={searchQuery}
            onSearchChange={(e) => updateSearchValue(e.target.value)}
            charCount={charCount}
            tooltip="Search by Order ID or Phone Number"
            placeholder="Search by Order ID or Phone Number..."
            error={Boolean(resolvedOrderSearch.blockFetch && resolvedOrderSearch.phoneSearchError)}
            helperText={resolvedOrderSearch.blockFetch ? resolvedOrderSearch.phoneSearchError : undefined}
          />
        </Box>

        {/* Filter - Fixed width */}
        <Box sx={{ flex: "0 0 auto" }}>
          <Filter
            fields={filterFields}
            onApply={(filters) => handleFiltersChange(filters)}
            loading={filterLoading}
          />
        </Box>

        {/* Export Button - Fixed width */}
        <Box sx={{ flex: "0 0 auto" }}>
          <ButtonOne
            variant="contained"
            onClick={handleExport}
            startIcon={<Download sx={{ fontSize: 18 }} />}
            sx={{
              ...typography.bodyLarge,
              backgroundColor: ColorsConstants.primaryPurple100,
              whiteSpace: "nowrap",
              "&:hover": {
                backgroundColor: ColorsConstants.primaryPurple100,
                opacity: 0.9,
              },
            }}
          >
            Export
          </ButtonOne>
        </Box>
      </Box>

      {/* Table using AppTable */}
      <AppTable
        columns={tableColumns}
        rows={orders}
        rowKey={(row, index) => index}
        onRowClick={(row) =>
          onNavigate("order_items", { orderId: row.id, currency: row.currency })
        }
        rowClickable={true}
        maxHeight={660}
        containerSx={{
          scrollbarWidth: "thin",
          scrollbarColor: "#C5CEDB #F3F6FB",
          "&::-webkit-scrollbar": {
            width: "8px",
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: "#F3F6FB",
            borderRadius: "8px",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#C5CEDB",
            borderRadius: "8px",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            backgroundColor: "#A8B4C7",
          },
        }}
        rowSx={rowSx}
        paperSx={{
          border: "1px solid #DCDFE5",
          borderRadius: "12px",
          mb: 2,
        }}
        emptyMessage="No orders found"
        count={rowCount}
        page={controller.page}
        rowsPerPage={controller.rowsPerPage}
        rowsPerPageOptions={[20, 50, 100]}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Box>
  );
}